import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Image, TouchableOpacity, Platform, Dimensions } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores } from '../../../../styles/variablesCss';
import AddCarrito from '../../Header/Carrito/AddCarrito';
import BanderaPromo from './BanderaPromo';
import { useNavigation } from '@react-navigation/native';
import { BACKEND_URL } from '../../../../Config/Const';
import { useCart } from '../../../../context/CartContext';
import { BotonFavorito } from '../../Header/AdminUsuario/Favoritos';
import { formatPrecio } from '../../../../utils/formatPrice';

const PlaceHolderProducto = ({
  foto,
  descripcion,
  tipo,
  nombre,
  precio,
  precioSinImp,
  preUnMed,
  plu,
  combo,
  oferta,
  cuotasSinInt,
  minStock, // Valor mínimo para incremento/decremento
  umedida, // Unidad de medida del producto
  cucarda, // Imágenes de promociones
  stock, // Stock disponible para el producto
  maxStock, // Límite máximo de compra (0 = sin límite)
  fotos, // Lista de fotos adicionales
  masInfo, // Información adicional del producto para la ficha
  faltante = false,
}) => {
  // Función de sanitización para evitar renderizar valores inválidos
  const safeText = (value, defaultValue = '') => {
    if (!value || value === '.' || value === null || value === undefined) return defaultValue;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '.') return defaultValue;
    return stringValue;
  };

  const safePrecio = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) ? num : 0;
};

  // Usar React Navigation para todas las plataformas
  const navigation = useNavigation();

  const { 
  verificarProductoEnCarrito: checkProductInCart,
  productos: productosEnCarrito
  } = useCart();

 
  const getInitialUnits = () => {
  const val = parseFloat(minStock);
  return !isNaN(val) && val > 0 ? val : 1;
  };
  const [unidades, setUnidades] = useState(getInitialUnits);
  
  // Estado para manejar errores de stock
  const [stockError, setStockError] = useState('');

  // Estados para el carrito
  const [estaEnCarrito, setEstaEnCarrito] = useState(false);
  const [cantidadEnCarrito, setCantidadEnCarrito] = useState(0);


  // Detectar si estamos en web o móvil
  const isWeb = Platform.OS === 'web';

  const imageWrapperRef = useRef(null);
  const [shouldLoadImage, setShouldLoadImage] = useState(!isWeb);

  useEffect(() => {
    if (!isWeb) return;
    if (shouldLoadImage) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoadImage(true);
      return;
    }

    const node = imageWrapperRef.current;
    if (!node) {
      setShouldLoadImage(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries[0];
        if (entry && entry.isIntersecting) {
          setShouldLoadImage(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isWeb, shouldLoadImage]);

  // Ancho de viewport (web usa window.innerWidth)
  const initialWidth = Platform.OS === 'web' && typeof window !== 'undefined'
    ? window.innerWidth
    : Dimensions.get('window').width;
  const [viewportWidth, setViewportWidth] = useState(initialWidth);

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const onResize = () => setViewportWidth(window.innerWidth);
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }
  }, []);

  // Calcular el ancho del ítem para responsive
const isSmallScreen = useMemo(() => viewportWidth < 600, [viewportWidth]);
  // La tarjeta ocupa siempre el 100% del ancho de su wrapper
  const itemWidth = '100%';

  // Calcular el límite máximo de unidades (basado en stock y maxStock)
  const calcularLimiteMaximo = () => {
    // Si maxStock es mayor a 0, lo usamos como límite, sin importar el stock
    if (maxStock && maxStock > 0) {
      return maxStock;
    }
    // Si no hay maxStock específico, el límite es el stock disponible
    return typeof stock === 'number' && stock > 0 ? stock : Infinity;
  };

  // Verificar si hay suficiente stock antes de incrementar
  const incrementar = () => {
    // Incrementar por el valor de minStock, pero respetando límites
    setUnidades(prev => {
      const incremento = minStock ? parseFloat(minStock) : 1;
      const nuevoValor = parseFloat(prev) + incremento;

      // Obtener el límite máximo permitido (basado en maxStock y stock)
      const limiteMaximo = calcularLimiteMaximo();

      // Verificar si el nuevo valor supera el límite máximo
      if (limiteMaximo !== Infinity && nuevoValor > limiteMaximo) {
        // Si excede el límite, mostramos el error y limitamos el valor
        setStockError(`¡Atención! Has alcanzado el límite máximo (${limiteMaximo}).`);
        return limiteMaximo;
      } else {
        // Si está dentro de los límites, limpiamos cualquier error
        setStockError('');
        return parseFloat(nuevoValor.toFixed(2));
      }
    });
  };

  const decrementar = () => {
    // Solo decrementar si el valor resultante sería mayor o igual que minStock
    const valorMinimo = minStock ? parseFloat(minStock) : 1;
    if (parseFloat(unidades) >= valorMinimo * 2) {
      setUnidades(prev => {
        const nuevoValor = parseFloat((parseFloat(prev) - valorMinimo).toFixed(2));

        // Calcular el límite máximo permitido
        const limiteMaximo = calcularLimiteMaximo();

        // Limpiar mensaje de error si estamos por debajo del límite
        if (limiteMaximo !== Infinity && nuevoValor <= limiteMaximo) {
          setStockError('');
        }
        return nuevoValor;
      });
    }
  };

  const navigateToProductDetail = (event) => {
    // Prevenir propagación del evento para evitar conflictos
    if (event) {
      event.stopPropagation();
    }

    // Crear un slug a partir de la descripción para la URL
    const nombreUrl = descripcion
      ? descripcion.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : 'producto';

    // Guardar información de navegación en sessionStorage para la página de detalle
    if (Platform.OS === 'web' && window.sessionStorage) {
      try {
        // Obtener datos de navegación del contexto global o props
        let navigationData = {
          searchText: '',
          departmentName: '',
          categoryName: '',
          subcategoryName: '',
          isBannerSearch: false
        };

        // Si tenemos acceso a estos datos desde props, los usamos
        if (window.navigationContext) {
          navigationData = {
            searchText: window.navigationContext.searchText || '',
            departmentName: window.navigationContext.departmentName || '',
            categoryName: window.navigationContext.categoryName || '',
            subcategoryName: window.navigationContext.subcategoryName || '',
            isBannerSearch: window.navigationContext.isBannerSearch || false
          };
        }

        // Guardar cada valor individualmente en sessionStorage
        window.sessionStorage.setItem('searchText', navigationData.searchText);
        window.sessionStorage.setItem('departmentName', navigationData.departmentName);
        window.sessionStorage.setItem('categoryName', navigationData.categoryName);
        window.sessionStorage.setItem('subcategoryName', navigationData.subcategoryName);
        window.sessionStorage.setItem('isBannerSearch', navigationData.isBannerSearch.toString());
      } catch (error) {
      }
    }

    // Crear un objeto con todos los datos del producto necesarios
    const productoData = {
      plu,
      descripcion,
      foto,
      fotos,
      precio,
      precioSinImp,
      preUnMed,
      tipo,
      nombre,
      combo,
      oferta,
      cuotasSinInt,
      minStock,
      umedida,
      stock,
      maxStock,
      cucarda,
      masInfo
    };

    // Usar React Navigation para móvil o React Router para web
    if (Platform.OS === 'web') {
      if (window && window.location) {
        // Guardar estado para restaurar listado al volver
        try {
          const returnState = {
            urlSearch: window.location.search || '',
            scrollY: window.scrollY || window.pageYOffset || 0,
            ts: Date.now(),
          };
          sessionStorage.setItem('productosReturnState', JSON.stringify(returnState));
        } catch (_) {}

        // Guardar datos del producto en sessionStorage para recuperarlos en la página de detalle
        sessionStorage.setItem('currentProductData', JSON.stringify(productoData));

        // Construir una URL amigable para web que coincida con la configuración de rutas
        const webUrl = `/producto/${plu}`;

        // Navegar sin recargar la aplicación (React Navigation Web manejará la URL vía linking)
        if (navigation && typeof navigation.navigate === 'function') {
          navigation.navigate('DescripcionProducto', {
            nombre: nombreUrl,
            plu: plu,
            producto: productoData,
          });
        } else {
          // Fallback si navigation no está disponible
          window.history.pushState({}, '', webUrl);
          window.dispatchEvent(new Event('popstate'));
          window.dispatchEvent(new Event('navigationChange'));
        }
      }
    } else if (navigation) {
      // Usar React Navigation para móvil
      navigation.navigate('DescripcionProducto', {
        nombre: nombreUrl,
        plu: plu,
        producto: productoData // Pasar el producto completo
      });
    }
  };

  // Manejador para actualizaciones del estado del carrito desde AddCarrito
  const handleCartStatusUpdate = (estaEnCart, cantidad) => {
    setEstaEnCarrito(estaEnCart);
    setCantidadEnCarrito(cantidad);
  };


  // Función para verificar si este producto específico está en el carrito
  const verificarProductoEnCarrito = useCallback(async () => {
    try {
      // Usar la función centralizada del contexto
      const result = await checkProductInCart(plu);

      // Actualizar el estado local con los resultados
      setEstaEnCarrito(result.estaEnCarrito);
      setCantidadEnCarrito(result.cantidad);

      return result.estaEnCarrito;
    } catch (err) {
      setEstaEnCarrito(false);
      setCantidadEnCarrito(0);
      return false;
    }
  }, [plu, checkProductInCart]);

  // Cargar el estado inicial del carrito para este producto
  useEffect(() => {
    if (plu) verificarProductoEnCarrito();
  }, [plu]);

  // Verificar si el producto está en el carrito cada vez que cambia la lista de productos del carrito
  useEffect(() => {
    if (plu && productosEnCarrito !== undefined) {
      const pluStr = String(plu);

      // Si el carrito está vacío, resetear el estado
      if (productosEnCarrito.length === 0) {
        setEstaEnCarrito(false);
        setCantidadEnCarrito(0);
      } else {
        // Si hay productos, buscar el producto actual
        const productoEnCarrito = productosEnCarrito.find(item => String(item.plu) === pluStr);

        if (productoEnCarrito) {
          setEstaEnCarrito(true);
          setCantidadEnCarrito(productoEnCarrito.cantCarrito || 1);
        } else {
          setEstaEnCarrito(false);
          setCantidadEnCarrito(0);
        }
      }
    }
  }, [plu, productosEnCarrito]); // Nos actualizamos cuando cambia el carrito en el contexto

  const comboTieneDatos = Array.isArray(combo) && combo.length > 0 && combo[0];
  const totalCantObligCombo = comboTieneDatos ? combo.reduce((sum, c) => sum + (c.cantOblig || 0), 0) : 0;
  const shouldShowComboQuantityText = comboTieneDatos
    ? combo[0].cancom > 1 && combo[0].tipoCombo !== 4 && totalCantObligCombo <= 1
    : false;

  // Verificar si este producto es el plu de regalo en un combo tipo 3
  const esPluRegalo = comboTieneDatos && combo[0].tipoCombo === 3 && plu === combo[0].pluRegalo;

  const minPurchaseQuantity = useMemo(() => {
    const val = parseFloat(minStock);
    return !isNaN(val) && val > 0 ? val : 1;
  }, [minStock]);

  const unitBasePrice = useMemo(() => safePrecio(precio), [precio]);
  const regularPriceToShow = useMemo(() => unitBasePrice * minPurchaseQuantity, [unitBasePrice, minPurchaseQuantity]);

  const comboBasePrice = useMemo(() => {
    if (!comboTieneDatos) return unitBasePrice;
    if (esPluRegalo) return unitBasePrice;

    const primerCombo = combo[0] || {};
    if (primerCombo.tipoCombo === 4 || totalCantObligCombo > 0) {
      return unitBasePrice;
    }

    return safePrecio(primerCombo.precom ?? unitBasePrice);
  }, [comboTieneDatos, esPluRegalo, combo, totalCantObligCombo, unitBasePrice]);

  const comboPriceToShow = useMemo(() => comboBasePrice * minPurchaseQuantity, [comboBasePrice, minPurchaseQuantity]);
  
  return (
    <View
      style={[
        styles.container,
        faltante ? styles.containerFaltante : null,
        { width: itemWidth },
        isSmallScreen ? { marginHorizontal: 0, minWidth: '100%' } : null
      ]}
    >
      <BanderaPromo
        combo={combo}
        oferta={oferta}
        onNavigateToDetail={navigateToProductDetail}
        producto={{
          plu,
          descripcion,
          foto,
          precio,
          precioSinImp,
          preUnMed,
          tipo,
          nombre,
          combo,
          oferta,
          cuotasSinInt,
          minStock,
          umedida,
          stock,
          maxStock,
          cucarda
        }}
      />

      <View style={styles.contentSection}>
        {/* Contenedor para la imagen principal y las cucardas */}
        <View style={styles.imageContainer}>
          {/* Imagen principal del producto - ahora clickeable */}
          <View ref={imageWrapperRef}>
            <TouchableOpacity
              onPress={navigateToProductDetail}
              activeOpacity={0.8}
            >
              {shouldLoadImage ? (
                <Image
                  source={typeof foto === 'string' ? { uri: foto } : foto}
                  style={[
                    styles.productImage,
                    isSmallScreen ? { width: 100, height: 150 } : null
                  ]}
                  resizeMode="contain"
                />
              ) : (
                <View
                  style={[
                    styles.productImage,
                    styles.productImagePlaceholder,
                    isSmallScreen ? { width: 100, height: 150 } : null
                  ]}
                />
              )}
            </TouchableOpacity>
          </View>

          {/* Botón de favoritos movido al lado del control de cantidad */}

          {/* Mostrar cucardas si existen */}
          {cucarda && Array.isArray(cucarda) && cucarda.length > 0 && (
            <View style={styles.cucardasContainer}>
              {cucarda.map((item, index) => {
                // Usar la misma lógica de BannerManager para procesar URLs
                if (item && item.imagen) {
                  const cucardaUrl = item.imagen.startsWith('http')
                    ? item.imagen
                    : `${BACKEND_URL}${item.imagen}`;

                  return (
                    <Image
                      key={`${plu}_cucarda_${index}`}
                      source={{ uri: cucardaUrl }}
                      style={styles.cucardaImage}
                      resizeMode="contain"
                    />
                  );
                }
                return null;
              })}
            </View>
          )}
        </View>

        <Text style={[styles.nombre, !isWeb && styles.nombreMobile, faltante ? styles.textFaltante : null]}>{safeText(descripcion, 'Producto')}</Text>

        {faltante && (
          <View style={styles.faltanteBadge}>
            <Text style={styles.faltanteBadgeText}>No disponible</Text>
          </View>
        )}

        {/* Mensaje de vencimiento o información adicional del combo */}
        {comboTieneDatos && safeText(combo[0].vencom) && (
          <Text style={styles.vencomMessage}>{safeText(combo[0].vencom)}</Text>
        )}

        {comboTieneDatos ? (
          <View style={styles.preciosContainer}>
            {shouldShowComboQuantityText && (
              <Text style={styles.comboCantidadText}>Llevando {combo[0].cancom} pagas c/u</Text>
            )}
            <Text style={styles.precioCombo}>$ {comboPriceToShow ? formatPrecio(comboPriceToShow) : '0.00'}</Text>
            {/* Mostrar precio tachado solo cuando oferta es N o D */}
            {cuotasSinInt > 0 && comboPriceToShow > 0 && (
              <View style={{ flexDirection: isSmallScreen ? 'column' : 'column', alignItems: 'center' }}>
                <Text style={styles.cuotasSinInteres}>{cuotasSinInt} x {formatPrecio(comboPriceToShow / cuotasSinInt)}</Text>
                <Text  style={styles.cuotasSinInteres}> sin interés</Text>
              </View>
            )}
            <Text style={styles.precioLitro}>{safeText(preUnMed, '0.00')}</Text>
            {precioSinImp > 0 && (
              <Text style={styles.precioLitro}>Precio sin impuestos nacionales: $ {formatPrecio(precioSinImp)}</Text>
            )}
          </View>
        ) : !faltante ? (
          <View style={styles.preciosContainer}>
            <Text style={styles.precio}>$ {regularPriceToShow ? formatPrecio(regularPriceToShow, true) : '0,00'}</Text>
            {masInfo && masInfo.length > 0 && minStock < 1 && safeText(masInfo[0].descripcion) && (
              <Text style={[styles.precioUnitario]}> Aprox: {safeText(masInfo[0].descripcion)}</Text>
            )}
            {cuotasSinInt > 0 && regularPriceToShow > 0 && (
              <View style={{  alignItems: 'center' }}>
                <Text style={{...styles.cuotasSinInteres, textAlign: isSmallScreen ? 'center' : 'left', }}>{cuotasSinInt} cuotas de $ {formatPrecio(regularPriceToShow / cuotasSinInt)}</Text>
                <Text  style={{...styles.cuotasSinInteres}}> sin interés</Text>
              </View>
            )}
            {safeText(preUnMed) && <Text style={styles.precioUnitario}>{safeText(preUnMed).replace('.', ',')}</Text>}
            {precioSinImp > 0 && <Text style={styles.precioSinImpuestos}>Precio sin impuestos nacionales: $ {formatPrecio(precioSinImp)}</Text>}

            {/* Indicador de producto en carrito */}
          </View>
        ) : null}
      </View>
        
      <View style={styles.controlsSection}>
        {/* Banner verde de producto en carrito */}
        {estaEnCarrito && (
          <View style={styles.enCarritoContainer}>
            <MaterialCommunityIcons name="cart-check" size={14} color={colores.verde} />
            <Text style={styles.enCarritoText}>Tenés ({cantidadEnCarrito})</Text>
          </View>
        )}

        <View style={[
          styles.cantidadContainer,
          isSmallScreen ? styles.cantidadContainerMobile : styles.cantidadContainerDesktop
        ]}>
          <View style={[
            styles.controlesGroup,
            isSmallScreen ? styles.controlesGroupMobile : styles.controlesGroupDesktop
          ]}>
            <View style={[styles.controles, isSmallScreen && styles.controlesMobile]}>
              <TouchableOpacity
                style={[styles.botonCantidad, { borderRightColor: colores.negro, borderRightWidth: 1 }]}
                onPress={decrementar}
                disabled={faltante}
              >
                <Text style={[styles.botonText, faltante ? styles.textoDeshabilitado : null]}>-</Text>
              </TouchableOpacity>

              <View style={styles.cantidadInput}>
                <Text style={styles.cantidadText}>
                  {typeof unidades === 'number'
                    ? (Number.isInteger(unidades)
                        ? unidades.toString()
                        : unidades.toFixed(2))
                    : unidades}
                </Text>

                {/* Mostrar máximo por compra cuando maxStock > 0 */}
                {maxStock > 0 && (
                  <Text style={styles.maxStockText}>por compra: {maxStock}</Text>
                )}
              </View>

              <TouchableOpacity
                style={[
                  styles.botonCantidad,
                  { borderLeftColor: colores.negro, borderLeftWidth: 1 },
                  // Deshabilitar visualmente el botón cuando se alcanza el límite máximo
                  ((maxStock > 0 && unidades >= maxStock) || (maxStock === 0 && stock > 0 && unidades >= stock)) && styles.botonDeshabilitado
                ]}
                onPress={incrementar}
                disabled={faltante || (maxStock > 0 && unidades >= maxStock) || (maxStock === 0 && stock > 0 && unidades >= stock)}
              >
                <Text style={[styles.botonText, (faltante || (stock > 0 && unidades >= stock)) && styles.textoDeshabilitado]}>+</Text>
              </TouchableOpacity>
            </View>

            {!isSmallScreen && (
              <View style={styles.favoritoContainer}>
                <BotonFavorito plu={plu} size={22} style={styles.favoritoInline} />
              </View>
            )}
          </View>

          {stockError ? (
            <View style={styles.stockErrorContainer}>
              <Text style={styles.stockErrorText}>{stockError}</Text>
            </View>
          ) : null}
        </View>
        {/* En mobile: favorito y carrito uno al lado del otro */}
        {isSmallScreen ? (
          <View style={styles.mobileActionsContainer}>
            <View style={styles.mobileFavoritoContainer}>
              <BotonFavorito plu={plu} size={22} style={styles.favoritoInline} />
            </View>
            <View style={styles.mobileCarritoContainer}>
              <AddCarrito
                plu={plu}
                cantidad={unidades}
                style={[styles.botonCarrito, styles.botonCarritoMobile]}
                disabled={faltante}
                onCartStatusUpdate={handleCartStatusUpdate}
                onSuccess={() => {
                  // Actualizar el estado cuando se agrega al carrito exitosamente
                  // Ya no es necesario manejar esto aquí, se hace en AddCarrito
                  // y se nos notifica vía onCartStatusUpdate
                }}
              />
            </View>
          </View>
        ) : (
          <View style={styles.carritoContainer}>
            <AddCarrito
              plu={plu}
              cantidad={unidades}
              style={[styles.botonCarrito, styles.botonCarritoDesktop]}
              disabled={faltante}
              onCartStatusUpdate={handleCartStatusUpdate}
              onSuccess={() => {
                // Actualizar el estado cuando se agrega al carrito exitosamente
                // Ya no es necesario manejar esto aquí, se hace en AddCarrito
                // y se nos notifica vía onCartStatusUpdate
              }}
            />
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colores.blanco,
    padding: 10,
    marginHorizontal: 5,
   //marginVertical: 5,
    width: '100%',
    //elevation: 2,
    position: 'relative',
    overflow: 'visible',
    //elevation: 5,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
    height: 'auto',
    minHeight: 0,
  },
  containerFaltante: {
    opacity: 0.55,
  },
  textFaltante: {
    color: '#666',
  },
  faltanteBadge: {
    marginTop: 6,
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 12,
    backgroundColor: '#E0E0E0',
  },
  faltanteBadgeText: {
    fontSize: 12,
    color: '#555',
    fontWeight: '700',
  },
  contentSection: {
    width: '100%',
    marginVertical: 5,
    alignItems: 'center',
    minHeight: 320,
    justifyContent: 'flex-start',
  },
  imageContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  cucardasContainer: {
    position: 'absolute',
    left: 10,
    bottom: 30,
    flexDirection: 'column',
    alignItems: 'flex-end',
    zIndex: 10, // Para asegurar que aparezca sobre otros elementos
  },
  cucardaImage: {
    width: 60,
    height: 60,
    marginBottom: 8,
    backgroundColor: 'transparent', // Para que no tenga fondo
  },
  controlsSection: {
    width: '100%',
    marginTop: 'auto',
    paddingTop: 15,
    paddingHorizontal: 10,
    paddingBottom: 5,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
    alignItems: 'center',
  },
  preciosContainer: {
    width: '100%',
    alignItems: 'center',
    marginTop: 10,
    minHeight: 80,
    justifyContent: 'flex-start',
  },
  precioUnitario: {
    fontSize: 11,
    color: colores.textoSecundario,
    marginTop: 3,
    maxHeight: 15,
  },
  precioSinImpuestos: {
    fontSize: 11,
    color: colores.textoSecundario,
    marginTop: 3,
    textAlign: 'center',
  },
  nombre: {
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
    height: 60,
    textTransform: 'uppercase',
    overflow: 'hidden',
  },
  precioCombo: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colores.rojo,
    marginBottom: 5,
  },
  precioNormal: {
    fontSize: 10,
    color: colores.textoSecundario,
    textDecorationLine: 'line-through',
    marginBottom: 5,
  },
  comboCantidadText: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colores.rojo,
    textAlign: 'center',
  },

  productImage: {
    width: Platform.OS === 'web' ? 170 : '90%',
    height: Platform.OS === 'web' ? 170 : 150,
    marginBottom: 10,
    //backgroundColor: '#f8f8f8',
    //borderWidth: 1,
    //borderColor: '#e5e5e5',
    // borderRadius: 8,
    padding: 5,
  },
  productImagePlaceholder: {
    backgroundColor: '#f2f2f2',
  },
  nombreMobile: {
    fontSize: 14,
    height: 60,
    overflow: 'hidden',
  },
  precio: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  cuotasSinInteres: {
    fontSize: 13,
    fontWeight: 'bold',
    color: colores.rojo,
  },
  precioLitro: {
    fontSize: 11,
    color: colores.textoSecundario,
    marginTop: 3,
    textAlign: 'center',
  },
  vencomMessage: {
    fontSize: 14,
    color: '#e30613',
    fontWeight: 'bold',
    marginTop: 5,
    marginBottom: 5,
    textAlign: 'center',
  },
  controlesGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  controlesGroupMobile: {
    width: 200,
    justifyContent: 'center',
  },
  controlesGroupDesktop: {
    width: 196,
  },
  controles: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colores.fondoSecundario,
    borderRadius: 5,
  },
  controlesMobile: {
    alignSelf: 'center',
  },
  stockErrorText: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 5,
    color: colores.rojo,
    fontWeight: 'bold',
  },
  botonCantidad: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colores.fondoSecundario,
  },
  botonText: {
    fontSize: 20,
    color: colores.textoSecundario,
  },
  cantidadContainer: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    justifyContent: 'center',
  },
  cantidadContainerMobile: {
    alignSelf: 'center',
  },
  cantidadContainerDesktop: {
    alignSelf: 'center',
  },
  favoritoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    width: 36,
    minWidth: 36,
    borderRadius: 18,
    overflow: 'hidden',
    zIndex: 0,
  },
  favoritoInline: {
    position: 'relative',
    top: 0,
    right: 0,
    backgroundColor: 'transparent',
    padding: 0,
    ...(Platform.OS === 'web' ? { boxShadow: 'none' } : { shadowOpacity: 0, elevation: 0 }),
  },
  cantidadInput: {
    width: 80, // Aumentar el ancho para que quepa la información adicional
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colores.fondoSecundario,
  },
  botonDeshabilitado: {
    opacity: 0.5,
    backgroundColor: colores.grisClaro,
  },
  textoDeshabilitado: {
    color: colores.gris,
  },
  stockErrorContainer: {
    marginVertical: 5,
    paddingHorizontal: 10,
    width: '100%',
    alignItems: 'center',
  },
  maxStockText: {
    fontSize: 10,
    color: colores.acento,
    marginTop: 2,
  },
  cantidadText: {
    fontSize: 16,
  },
  carritoContainer: {
    width: '100%',
    alignSelf: 'center',
    alignItems: 'center',
    marginTop: 12,
  },
  mobileActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginTop: 12,
    gap: 8,
    width: 200,
  },
  mobileFavoritoContainer: {
    width: 44,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: colores.fondoSecundario,
    borderRadius: 5,
    flexShrink: 0,
  },
  mobileCarritoContainer: {
    width: 90, // mismo ancho que los controles (36+80+36)
    alignItems: 'stretch',
  },
  botonCarrito: {
    backgroundColor: colores.rojo,
    height: 40,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 5,
    alignSelf: 'center',
  },
  botonCarritoMobile: {
    width: '100%',
  },
  botonCarritoDesktop: {
    width: 196, // Controles (152) + gap (8) + favorito (36)
  },
  // Se eliminó el borde verde para los productos en carrito
  enCarritoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e8f5e9',
    padding: 8,
    marginBottom: 10,
    borderRadius: 4,
    width: '100%',
    justifyContent: 'center',
  },
  enCarritoText: {
    marginLeft: 4,
    color: colores.verde,
    fontSize: 10,
    fontWeight: '500',
  },
});

export default PlaceHolderProducto;
